import { useState, useContext, useEffect } from 'react';
import { GlobalContext } from 'GlobalContext';
import { message, Spin, Tooltip, Upload, Popconfirm } from 'antd';
import api from 'api';
import { get } from 'utils/fetch';
import { isPhoneSize } from 'utils/util';
import {
    FileOutlined,
    CloseCircleFilled,
    PlusOutlined,
} from '@ant-design/icons';

function Files(props) {
    const { approvedPlanPackage, setApprovedPlanPackage, editMode } = props;
    const { manageAssetId, setManageAssetId, accessToken, refreshSession } =
        useContext(GlobalContext);
    const [messageApi, contextHolder] = message.useMessage();

    const handleDelete = async (index) => {
        const urlToDelete = approvedPlanPackage[index];

        try {
            // setLoading(true);
            // Call backend to delete from S3 and DB
            const res = await fetch(
                api.asset.deleteApprovedPlanFile(manageAssetId),
                {
                    method: 'DELETE', // or DELETE
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${accessToken}`,
                    },
                    body: JSON.stringify({ fileUrl: urlToDelete }),
                }
            );
            const data = await res.json();

            if (data.success) {
                // Update local state
                setApprovedPlanPackage((prev) =>
                    prev.filter((_, i) => i !== index)
                );
                message.success('File deleted successfully');
            } else {
                message.error('Failed to delete file');
            }
        } catch (err) {
            console.error(err);
            message.error('Error deleting file');
        } finally {
            // setLoading(false);
        }
    };

    async function upsert(newFileList) {
        const body = JSON.stringify({
            approvedPlanPackage: newFileList,
        });

        const options = {
            method: 'POST', // *GET, POST, PUT, DELETE, etc.
            mode: 'cors', // no-cors, *cors, same-origin
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${accessToken}`,
            },
            body,
        };
        const response = await fetch(
            api.asset.upsertDesignApprovedPlanPackage(manageAssetId),
            options
        );
        const data = await response.json();
        const idRes = data.data;
        refreshSession();
        if (idRes === 0) {
            messageApi.error({
                type: 'error', // or 'error', 'info', 'warning', 'loading'
                content: 'files update failed',
                duration: 3, // seconds (optional)
            });
        } else {
            messageApi.open({
                type: 'success', // or 'error', 'info', 'warning', 'loading'
                content: 'files update success',
                duration: 3, // seconds (optional)
            });
        }
    }

    const uploadProps = {
        multiple: true,
        showUploadList: false, // hide default UI
        beforeUpload: () => false, // prevent auto-upload
        async onChange(info) {
            const files = info.fileList.map((f) => f.originFileObj);

            const uploadedUrls = [];

            for (const file of files) {
                try {
                    const formData = new FormData();
                    formData.append('file', file);
                    const filename = `${file.name}`;

                    // 1. Request a presigned URL
                    const presignRes = await fetch(api.asset.assetUploadFile, {
                        method: 'POST',
                        body: formData,
                        headers: {
                            Authorization: `Bearer ${accessToken}`,
                        },
                    });
                    const { url } = await presignRes.json();

                    uploadedUrls.push(url);
                } finally {
                }
            }

            // 3. Save URLs to state
            const newFileList = Array.from(
                new Set([...approvedPlanPackage, ...uploadedUrls])
            );
            setApprovedPlanPackage(newFileList);
            upsert(newFileList);
        },
    };

    return (
        <div
            style={{
                display: 'flex',
                gap: '2px',
                flexWrap: 'wrap',
                alignItems: 'center',
            }}
        >
            {approvedPlanPackage.map((file, idx) => (
                <div
                    key={idx}
                    style={{
                        position: 'relative',
                        width: '40px',
                        height: '40px',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                    }}
                >
                    {/* Delete Button */}
                    {editMode && (
                        <Popconfirm
                            title="Are you sure you want to delete this file?"
                            onConfirm={() => handleDelete(idx)}
                            okText="Yes"
                            cancelText="No"
                            placement="topRight"
                        >
                            <Tooltip title="Delete file">
                                <CloseCircleFilled
                                    // onClick={() => handleDelete(idx)}
                                    style={{
                                        position: 'absolute',
                                        top: '-2px',
                                        right: '-0px',
                                        fontSize: '12px',
                                        color: 'red',
                                        cursor: 'pointer',
                                    }}
                                />
                            </Tooltip>
                        </Popconfirm>
                    )}

                    {/* File Icon */}
                    <Tooltip title={file.split('/').pop()}>
                        <a
                            href={file}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: 'inherit' }}
                        >
                            <FileOutlined style={{ fontSize: '32px' }} />
                        </a>
                    </Tooltip>
                </div>
            ))}

            {/* Plus Button for Upload */}
            {editMode && (
                <Upload {...uploadProps}>
                    <div
                        style={{
                            width: '40px',
                            height: '40px',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            border: '1px dashed #aaa',
                            borderRadius: '6px',
                            cursor: 'pointer',
                        }}
                    >
                        <PlusOutlined style={{ fontSize: '20px' }} />
                    </div>
                </Upload>
            )}
        </div>
    );
}

export function DesignInfo() {
    const { manageAssetId, setManageAssetId, accessToken, refreshSession } =
        useContext(GlobalContext);
    const [messageApi, contextHolder] = message.useMessage();
    const [propertyDesignInfo, setPropertyDesignInfo] = useState(null);
    const [editMode, setEditMode] = useState(false);
    const [loading, setLoading] = useState(true);

    // fields
    const [approvedPlanPackage, setApprovedPlanPackage] = useState([]);
    const [targetPermitDate, setTargetPermitDate] = useState(null);

    const entryStyle = {
        margin: '20px',
        fontSize: '20px',
    };

    const inputStyle = {
        border: '0',
        outline: 'none',
        borderBottom: '1px solid black',
        fontSize: '20px',
        background: 'transparent',
    };

    const displayErrorMessage = (message) => {
        messageApi.open({
            type: 'error',
            content: message,
        });
    };

    async function fetchPropertyDesignInfo(manageAssetId) {
        await get(
            api.asset.propertyDesignInfo(manageAssetId),
            setPropertyDesignInfo,
            (errors) =>
                errors.forEach((err) => {
                    displayErrorMessage(err.message);
                }),
            accessToken
        );
        refreshSession();
        setLoading(false);
    }

    useEffect(() => {
        if (propertyDesignInfo) {
            restore(propertyDesignInfo);
            setApprovedPlanPackage(
                propertyDesignInfo?.approved_plan_package || []
            );
        }
    }, [propertyDesignInfo]);

    useEffect(() => {
        if (manageAssetId < 0) {
            return;
        }
        fetchPropertyDesignInfo(manageAssetId);
    }, []);

    function restore(propertyDesignInfo) {
        setTargetPermitDate(propertyDesignInfo?.target_permit_date);
    }

    function unchanged() {
        if (!propertyDesignInfo) return false;
        return targetPermitDate === propertyDesignInfo.target_permit_date;
    }

    async function upsert() {
        if (unchanged()) return;
        setLoading(true);
        const body = JSON.stringify({
            targetPermitDate,
        });

        const options = {
            method: 'POST', // *GET, POST, PUT, DELETE, etc.
            mode: 'cors', // no-cors, *cors, same-origin
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${accessToken}`,
            },
            body,
        };
        const response = await fetch(
            api.asset.upsertDesign(manageAssetId),
            options
        );
        const data = await response.json();
        const idRes = data.data;
        refreshSession();
        if (idRes === 0) {
            messageApi.error({
                type: 'error', // or 'error', 'info', 'warning', 'loading'
                content: propertyDesignInfo?.id
                    ? 'Loan insertion failed'
                    : 'Loan update failed',
                duration: 3, // seconds (optional)
            });
        } else {
            messageApi.open({
                type: 'success', // or 'error', 'info', 'warning', 'loading'
                content: propertyDesignInfo?.id
                    ? 'Loan insertion success'
                    : 'Loan update success',
                duration: 3, // seconds (optional)
            });
            fetchPropertyDesignInfo(manageAssetId);
        }
        setEditMode(false);
    }

    async function deleteDesign() {
        setLoading(true);

        const options = {
            method: 'DELETE',
            mode: 'cors',
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${accessToken}`,
            },
        };

        const response = await fetch(
            api.asset.deleteDesign(manageAssetId),
            options
        );
        const data = await response.json();
        const idRes = data.data;
        refreshSession();

        if (idRes === 0 || !idRes) {
            messageApi.error({
                type: 'error',
                content: 'Loan deletion failed',
                duration: 3,
            });
        } else {
            messageApi.open({
                type: 'success',
                content: 'Loan deletion success',
                duration: 3,
            });
        }
        setPropertyDesignInfo(null);
        restore(null);
        setApprovedPlanPackage([]);
        setLoading(false);
    }

    return (
        <>
            {contextHolder}
            {loading && (
                <div style={{ marginTop: '30px', textAlign: 'center' }}>
                    <Spin />
                </div>
            )}
            {!loading && (
                <div>
                    <div
                        style={{
                            width: 'fit-content',
                            marginLeft: '20px',
                            height: '38px',
                            position: 'fixed',
                            background: 'white',
                            width: '100%',
                            alignContent: 'end',
                            top: 40,
                        }}
                    >
                        <div
                            style={{
                                color: 'blue',
                                textDecoration: 'underline',
                                cursor: 'pointer',
                                width: 'fit-content',
                            }}
                            onClick={(e) => {
                                if (editMode) {
                                    restore(propertyDesignInfo);
                                }
                                setEditMode(!editMode);
                            }}
                        >
                            {editMode ? 'Restore' : 'Edit'}
                        </div>
                    </div>

                    <div
                        style={
                            isPhoneSize()
                                ? entryStyle
                                : {
                                      ...entryStyle,
                                      display: 'flex',
                                      marginTop: '50px',
                                  }
                        }
                    >
                        {editMode && (
                            <>
                                <div>Target Permit Date: </div>
                                <input
                                    type="date"
                                    style={inputStyle}
                                    value={targetPermitDate || ''}
                                    onInput={(e) => {
                                        setTargetPermitDate(e.target.value);
                                    }}
                                ></input>
                            </>
                        )}

                        {!editMode &&
                            `Target Permit Date: ${targetPermitDate || ''}`}
                    </div>

                    <div
                        style={
                            isPhoneSize()
                                ? entryStyle
                                : {
                                      ...entryStyle,
                                      display: 'flex',
                                  }
                        }
                    >
                        <>
                            <div>Approved Plan Package: </div>
                            <Files
                                approvedPlanPackage={approvedPlanPackage}
                                setApprovedPlanPackage={setApprovedPlanPackage}
                                editMode={editMode}
                            />
                        </>
                    </div>

                    <div style={{ display: 'flex', marginLeft: '20px' }}>
                        <button
                            style={{
                                height: '30px',
                                width: '60px',
                                borderRadius: '30px',
                                background: 'rgb(17 32 86)',
                                color: 'white',
                                cursor: 'pointer',
                            }}
                            onClick={upsert}
                        >
                            Save
                        </button>
                        <button
                            style={{
                                height: '30px',
                                width: '60px',
                                borderRadius: '30px',
                                background: 'rgb(86 17 17)',
                                color: 'white',
                                cursor: 'pointer',
                            }}
                            onClick={() => {
                                if (
                                    window.confirm(
                                        'Are you sure you want to delete this loan?'
                                    )
                                ) {
                                    deleteDesign();
                                }
                            }}
                        >
                            Delete
                        </button>
                    </div>
                </div>
            )}
        </>
    );
}
